﻿/**
 * InteractiveButtons â€” renders WhatsApp-style action buttons.
 */

import React, { useState, useMemo } from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useTheme, type ThemeColors } from '../../utils/theme';
import { FONT_SIZE, SPACING, RADIUS } from '../../utils/design-tokens';

interface Button {
  id: string;
  title: string;
}

interface Props {
  buttons: Button[];
  footer?: string;
  onPress: (buttonId: string, buttonLabel: string) => void;
}

export default function InteractiveButtons({ buttons, footer, onPress }: Props) {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const s = useMemo(() => createStyles(colors), [colors]);
  const [pressedId, setPressedId] = useState<string | null>(null);

  const handlePress = (btnId: string, buttonLabel: string) => {
    if (pressedId) return; // already pressed
    setPressedId(btnId);
    onPress(btnId, buttonLabel);
  };

  return (
    <View style={s.container}>
      {footer ? <Text style={s.footer}>{footer}</Text> : null}
      {buttons.map((btn) => {
        const isPressed = pressedId === btn.id;
        const isDisabled = !!pressedId;
        return (
          <TouchableOpacity
            key={btn.id}
            style={[s.button, isDisabled && s.buttonDisabled, isPressed && s.buttonPressed]}
            onPress={() => handlePress(btn.id, btn.title)}
            activeOpacity={0.7}
            disabled={isDisabled}
            accessibilityRole="button"
            accessibilityLabel={btn.title}
            hitSlop={8}
          >
            <Text style={[s.buttonText, isDisabled && s.buttonTextDisabled, isPressed && s.buttonTextPressed]}>
              {isPressed ? `${t('chat.sendingSelection')} ${btn.title}` : btn.title}
            </Text>
          </TouchableOpacity>
        );
      })}
    </View>
  );
}

const createStyles = (colors: ThemeColors) => StyleSheet.create({
  container: {
    marginTop: SPACING.sm,
  },
  footer: {
    fontSize: FONT_SIZE.xs,
    color: colors.textSecondary,
    marginBottom: 6,
  },
  button: {
    backgroundColor: colors.primaryLight,
    borderRadius: RADIUS.sm,
    paddingVertical: SPACING.md,
    paddingHorizontal: SPACING.base,
    marginTop: SPACING.xs,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: colors.primary,
  },
  buttonDisabled: {
    backgroundColor: colors.surfaceTinted,
    borderColor: colors.border,
  },
  buttonPressed: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
  },
  buttonText: {
    fontSize: FONT_SIZE.sm,
    fontWeight: '600',
    color: colors.primary,
  },
  buttonTextDisabled: {
    color: colors.textSecondary,
  },
  buttonTextPressed: {
    color: '#fff',
  },
});
